import { Box, Button, Chip, Divider, Fade, Typography, useTheme } from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import LucideIcon from "../../../../components/LucideIcon";
import IdealCustomerProfileDialog from "../../../content/IdealCustomerProfileDialog";
import { hostname } from "../../../../utils";
import { IcpResult } from "../types";
import StepNavButtons from "./StepNavButtons";

interface IcpPreviewStepProps {
  icp?: IcpResult;
  // The company the generated ICP belongs to — needed to fetch/save the full profile.
  companyId: string;
  getToken: () => Promise<string>;
  // Called with the re-summarized ICP after a save so the preview reflects the edits.
  onIcpSaved: (icp: IcpResult) => void;
  onNext: () => void;
  onBack: () => void;
}

// Re-summarize a full ICP (the shape the system's editor saves) down to the few fields the
// onboarding preview shows. Mirrors the backend's _summarize_icp so the card looks the same
// before and after an edit. Personas keep title + a one-line summary (their profile text);
// challenges come from the painPoints list.
function summarizeIcp(full: any): IcpResult {
  const personas = Array.isArray(full?.personas) ? full.personas : [];
  const painPoints = Array.isArray(full?.challenges?.painPoints)
    ? full.challenges.painPoints
    : [];
  return {
    id: full?.id,
    name: full?.name,
    industry: full?.industry,
    companySize: full?.companySize,
    description: full?.description,
    personas: personas.slice(0, 3).map((p: any) => ({
      title: p?.name || p?.title || "",
      summary: p?.profile || p?.role || "",
    })),
    challenges: painPoints.slice(0, 4),
  };
}

// Preview of the generated ICP. Read-only display with an Edit button that opens the same
// IdealCustomerProfileDialog the Content engine uses, loaded with the FULL profile — so
// onboarding edits the exact same data model (and saves the same way) as everywhere else,
// rather than a lossy summary.
export default function IcpPreviewStep({
  icp,
  companyId,
  getToken,
  onIcpSaved,
  onNext,
  onBack,
}: IcpPreviewStepProps) {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  // The full ICP loaded into the editor. We fetch it on Edit so the dialog sees every field
  // (businessProfile, personas, goToMarket, …), not just the summary the preview holds.
  const [fullIcp, setFullIcp] = useState<any | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const openEditor = async () => {
    if (!icp?.id || !companyId) return;
    setLoadingEdit(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${hostname}/content/profiles/${icp.id}`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFullIcp(res.data);
      setDialogOpen(true);
    } catch (err) {
      console.error("[IcpPreviewStep] failed to load ICP for editing", err);
    } finally {
      setLoadingEdit(false);
    }
  };

  // Save through the same path the Content engine uses (POST /content/profiles), including the
  // UI→DB field renames (businessProfile.characteristics, goToMarket.content). On success we
  // re-summarize the saved profile so the preview card updates in place.
  const handleSave = async (profileData: any) => {
    setSaving(true);
    try {
      const processedData = {
        ...profileData,
        id: profileData.id && profileData.id !== "null" ? profileData.id : undefined,
        businessProfile: {
          ...profileData.businessProfile,
          characteristics: profileData.businessProfile?.businessCharacteristics || [],
        },
        goToMarket: {
          ...profileData.goToMarket,
          content: profileData.goToMarket?.contentStrategies || [],
        },
      };
      const token = await getToken();
      await axios.post(
        `${hostname}/content/profiles`,
        { companyId, profile: processedData },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      onIcpSaved(summarizeIcp(processedData));
    } catch (err) {
      console.error("[IcpPreviewStep] failed to save ICP", err);
    } finally {
      setSaving(false);
      setDialogOpen(false);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          Lead Generation
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Your Ideal Customer Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          Here's who we think you should be targeting — generated from your business.
        </Typography>

        {!icp ? (
          <Typography color="text.secondary">
            We couldn't generate a profile this time. You can build one anytime from the
            Content engine.
          </Typography>
        ) : (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.hover",
            }}
          >
            {/* Industry/size chips, with the Edit button on its own line above so it never
                pushes the chips off the edge of the card on narrow screens. */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                size="small"
                onClick={openEditor}
                disabled={loadingEdit || !icp.id}
                startIcon={<LucideIcon name="Pencil" size={16} />}
                sx={{ textTransform: "none" }}
              >
                {loadingEdit ? "Loading…" : "Edit"}
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {icp.industry && <Chip label={icp.industry} color="primary" size="small" />}
              {icp.companySize && <Chip label={icp.companySize} variant="outlined" size="small" />}
            </Box>

            {icp.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                {icp.description}
              </Typography>
            )}

            {!!icp.personas?.length && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Key personas
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {icp.personas.map((p, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1.5 }}>
                      <LucideIcon name="UserCircle2" size={20} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p.title}
                        </Typography>
                        {p.summary && (
                          <Typography variant="caption" color="text.secondary">
                            {p.summary}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {!!icp.challenges?.length && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Pain points we'll help you solve
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {icp.challenges.map((c, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1 }}>
                      <LucideIcon name="Check" size={18} color={theme.palette.primary.main} />
                      <Typography variant="body2" color="text.secondary">
                        {c}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />

        <IdealCustomerProfileDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          companyId={companyId}
          existingData={fullIcp}
          onSave={handleSave}
          loading={saving}
        />
      </Box>
    </Fade>
  );
}
